import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.180.0/build/three.module.js';

/* ============================================================ utils */
const TOUCH = matchMedia('(pointer: coarse)').matches;
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const lerp = (a, b, t) => a + (b - a) * t;
const sst = (a, b, v) => { const t = clamp((v - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); };

let seed = 1937;
function rand() { seed = (seed * 16807) % 2147483647; return (seed - 1) / 2147483646; }
const rrange = (a, b) => a + (b - a) * rand();

function n2(ix, iz) { const s = Math.sin(ix * 127.1 + iz * 311.7) * 43758.5453; return s - Math.floor(s); }
function vnoise(x, z) {
  const ix = Math.floor(x), iz = Math.floor(z), fx = x - ix, fz = z - iz;
  const u = fx * fx * (3 - 2 * fx), v = fz * fz * (3 - 2 * fz);
  return lerp(lerp(n2(ix, iz), n2(ix + 1, iz), u), lerp(n2(ix, iz + 1), n2(ix + 1, iz + 1), u), v);
}
const fbm = (x, z) => vnoise(x, z) * 0.55 + vnoise(x * 2.13 + 7.3, z * 2.13 + 3.1) * 0.28 + vnoise(x * 4.31 + 13.7, z * 4.31 + 9.2) * 0.17;

/* ============================================================ terrain height */
function terrainH(x, z) {
  let h = -40 * sst(14, -170, z);          // main descent from the crest into the valley
  h -= 10 * sst(-10, -80, z);              // steeper mid-slope, like the real hill
  h -= 6 * sst(26, 90, z);                 // ground falls away behind the crest too
  h -= 8 * sst(30, 95, Math.abs(x)) * (1 - sst(-100, -190, z)); // ridge falloff to the sides
  // rolling valley floor + far hills (kept out of the walkable vineyard box)
  const dx = Math.max(Math.abs(x) - 26, 0);
  const dz = Math.max(-66 - z, 0) + Math.max(z - 26, 0);
  const fade = sst(8, 80, Math.hypot(dx, dz));
  h += (fbm(x * 0.008 + 3.1, z * 0.008 + 7.7) - 0.5) * 18 * fade;
  h += (fbm(x * 0.0031 + 11.4, z * 0.0031 + 5.2) - 0.5) * 34 * fade * sst(-100, -320, z);
  // Serralunga ridge — far back and off to the left so the town stays hidden
  // behind the crest fold + vine rows until the walk descends the aisle
  h += 6 * Math.exp(-((x + 102) ** 2 + (z + 420) ** 2) / (2 * 95 * 95));
  h += 3 * Math.exp(-((x + 100) ** 2 + (z + 418) ** 2) / (2 * 42 * 42));
  // enclosing rim of hills near the horizon
  const r = Math.hypot(x, z);
  h += sst(480, 760, r) * (22 + 16 * fbm(x * 0.004 + 1, z * 0.004 + 2));
  return h;
}

/* ============================================================ renderer / scene */
const canvas = document.getElementById('scene');
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
renderer.setPixelRatio(Math.min(devicePixelRatio, TOUCH ? 1.5 : 2));
renderer.setSize(innerWidth, innerHeight);
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.12;
if (!TOUCH) {
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
}

const scene = new THREE.Scene();
scene.fog = new THREE.Fog(0xe3e8e2, 90, 620);

const camera = new THREE.PerspectiveCamera(52, innerWidth / innerHeight, 0.1, 2600);
camera.rotation.order = 'YXZ';

const EYE = 1.8;
camera.position.set(0, terrainH(0, 15) + EYE, 15);
camera.rotation.set(-0.06, 0, 0);

/* ============================================================ lights */
const hemi = new THREE.HemisphereLight(0xd8e6ec, 0x6b7a44, 1.2);
scene.add(hemi);

const sun = new THREE.DirectionalLight(0xfff0d0, 2.3);
sun.position.set(-140, 190, -80);
sun.target.position.set(0, 0, -30);
scene.add(sun, sun.target);
if (!TOUCH) {
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  const sc = sun.shadow.camera;
  sc.left = -55; sc.right = 55; sc.top = 70; sc.bottom = -70;
  sc.near = 40; sc.far = 500;
  sun.shadow.bias = -0.0006;
}

/* ============================================================ shared world FX
   one time uniform drives leaf wind, grass sway and drifting cloud shadows */
const uTime = { value: 0 };

const cloudTex = (() => {
  const S = 512, c = document.createElement('canvas'); c.width = c.height = S;
  const g = c.getContext('2d');
  g.fillStyle = '#000'; g.fillRect(0, 0, S, S);
  for (let i = 0; i < 15; i++) {
    const cx = rand() * S, cy = rand() * S, blobs = 5 + (rand() * 5 | 0);
    for (let b = 0; b < blobs; b++) {
      const x = cx + rrange(-70, 70), y = cy + rrange(-40, 40), r = rrange(28, 78);
      for (const ox of [-S, 0, S]) for (const oy of [-S, 0, S]) {
        const gr = g.createRadialGradient(x + ox, y + oy, 0, x + ox, y + oy, r);
        gr.addColorStop(0, 'rgba(255,255,255,0.55)');
        gr.addColorStop(1, 'rgba(255,255,255,0)');
        g.fillStyle = gr; g.fillRect(x + ox - r, y + oy - r, r * 2, r * 2);
      }
    }
  }
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  return t;
})();

function enhance(mat, { sway = 0, swayMode = 'leaf', cloud = true } = {}) {
  mat.onBeforeCompile = (sh) => {
    sh.uniforms.uTime = uTime;
    if (cloud) sh.uniforms.uCloud = { value: cloudTex };
    let swayCode = '';
    if (sway > 0) {
      const weight = swayMode === 'grass' ? 'clamp(position.y * 2.2, 0.0, 1.0)' : '1.0';
      swayCode = `
        #ifdef USE_INSTANCING
          vec3 _ip = vec3(instanceMatrix[3][0], instanceMatrix[3][1], instanceMatrix[3][2]);
          float _ph = _ip.x * 0.61 + _ip.z * 0.53;
          float _sw = sin(uTime * 1.9 + _ph) * 0.6 + sin(uTime * 3.1 + _ph * 1.7) * 0.4;
          float _w = ${weight};
          transformed.x += ${sway.toFixed(3)} * _sw * _w;
          transformed.z += ${sway.toFixed(3)} * 0.6 * sin(uTime * 2.4 + _ph * 2.1) * _w;
        #endif`;
    }
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nuniform float uTime;\nvarying vec3 vWpos;')
      .replace('#include <begin_vertex>', `#include <begin_vertex>${swayCode}`)
      .replace('#include <project_vertex>', `#include <project_vertex>
        #ifdef USE_INSTANCING
          vWpos = (modelMatrix * instanceMatrix * vec4(transformed, 1.0)).xyz;
        #else
          vWpos = (modelMatrix * vec4(transformed, 1.0)).xyz;
        #endif`);
    if (cloud) {
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', '#include <common>\nuniform float uTime;\nuniform sampler2D uCloud;\nvarying vec3 vWpos;')
        .replace('#include <map_fragment>', `#include <map_fragment>
          float _cl = texture2D(uCloud, vWpos.xz * 0.0016 + vec2(uTime * 0.0052, uTime * 0.0021)).r;
          diffuseColor.rgb *= (1.0 - 0.22 * _cl);`);
    }
  };
  mat.customProgramCacheKey = () => `fx-${sway}-${swayMode}-${cloud}`;
  return mat;
}

/* ============================================================ sky */
const sky = new THREE.Mesh(
  new THREE.SphereGeometry(1800, 40, 20),
  new THREE.ShaderMaterial({
    side: THREE.BackSide, fog: false, depthWrite: false,
    uniforms: { uTime },
    vertexShader: `
      varying vec3 vDir;
      void main() {
        vDir = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: `
      uniform float uTime;
      varying vec3 vDir;
      float h21(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float vn(vec2 p) {
        vec2 i = floor(p), f = fract(p);
        vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h21(i), h21(i + vec2(1, 0)), u.x),
                   mix(h21(i + vec2(0, 1)), h21(i + vec2(1, 1)), u.x), u.y);
      }
      float fbm(vec2 p) {
        return vn(p) * 0.5 + vn(p * 2.1 + 7.0) * 0.27 + vn(p * 4.3 + 3.0) * 0.15 + vn(p * 8.9 + 11.0) * 0.08;
      }
      void main() {
        vec3 d = normalize(vDir);
        float h = max(d.y, 0.0);
        vec3 zen = vec3(0.30, 0.48, 0.70);
        vec3 hor = vec3(0.84, 0.88, 0.88);
        vec3 col = mix(hor, zen, pow(h, 0.75));
        vec3 sunDir = normalize(vec3(-0.42, 0.5, -0.55));
        float g = pow(max(dot(d, sunDir), 0.0), 5.0);
        col += vec3(1.0, 0.86, 0.55) * g * 0.28;
        col += vec3(1.0, 0.95, 0.8) * pow(max(dot(d, sunDir), 0.0), 90.0) * 0.7;
        vec2 cuv = d.xz / max(d.y, 0.06);
        vec2 drift = vec2(uTime * 0.0045, uTime * 0.0016);
        float cl = fbm(cuv * 0.85 + drift);
        float bank = smoothstep(0.48, 0.8, cl) * smoothstep(0.0, 0.09, d.y);
        col = mix(col, vec3(0.97, 0.97, 0.96), bank * 0.6);
        float shade = smoothstep(0.6, 0.86, fbm(cuv * 0.85 + drift + 3.7));
        col = mix(col, vec3(0.62, 0.66, 0.70), shade * 0.4 * smoothstep(0.0, 0.14, d.y));
        gl_FragColor = vec4(col, 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`
  })
);
scene.add(sky);

/* ============================================================ ground texture (patchwork valley) */
const WORLD = 1500;
function worldToCanvas(x, z, S) { return [ (x + WORLD / 2) / WORLD * S, (1 - (z + WORLD / 2) / WORLD) * S ]; }

const groundTex = (() => {
  const S = 2048, c = document.createElement('canvas'); c.width = c.height = S;
  const g = c.getContext('2d');
  const px = S / WORLD; // pixels per metre
  g.fillStyle = '#87975a'; g.fillRect(0, 0, S, S);

  const palette = ['#7d9150', '#93a35a', '#a8ab5e', '#c2b25c', '#b19a4b', '#6f8a49', '#87975a', '#9fae66', '#c8bb6a', '#708b42', '#95a052', '#bcae5e'];
  // patchwork fields
  for (let i = 0; i < 320; i++) {
    const wx = rrange(-WORLD / 2, WORLD / 2), wz = rrange(-WORLD / 2, WORLD / 2);
    if (Math.abs(wx) < 45 && wz > -110 && wz < 45) continue; // keep the vineyard hill clean
    const [cx, cy] = worldToCanvas(wx, wz, S);
    const w = rrange(35, 150) * px, h = rrange(28, 110) * px, a = rrange(0, Math.PI);
    g.save(); g.translate(cx, cy); g.rotate(a);
    g.fillStyle = palette[(rand() * palette.length) | 0];
    g.globalAlpha = rrange(0.7, 0.95);
    g.fillRect(-w / 2, -h / 2, w, h);
    // some parcels are vineyards: fine stripes
    if (rand() < 0.34) {
      g.globalAlpha = 0.5; g.strokeStyle = 'rgba(60,80,40,0.8)'; g.lineWidth = 1;
      for (let s = -w / 2; s < w / 2; s += 4.5 * px) {
        g.beginPath(); g.moveTo(s, -h / 2); g.lineTo(s, h / 2); g.stroke();
      }
    }
    g.globalAlpha = 0.65; g.strokeStyle = '#5a7040'; g.lineWidth = 1.6;
    g.strokeRect(-w / 2, -h / 2, w, h);
    g.restore();
  }
  // hedgerow scribbles
  g.globalAlpha = 0.8;
  for (let i = 0; i < 130; i++) {
    const wx = rrange(-WORLD / 2, WORLD / 2), wz = rrange(-WORLD / 2, WORLD / 2);
    if (Math.abs(wx) < 45 && wz > -110 && wz < 45) continue;
    let [x, y] = worldToCanvas(wx, wz, S);
    const a = rrange(0, Math.PI * 2), n = 6 + (rand() * 10 | 0);
    g.fillStyle = rand() < 0.5 ? '#47603a' : '#3d5634';
    for (let b = 0; b < n; b++) {
      const r = rrange(1.6, 3.4);
      g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill();
      x += Math.cos(a) * r * 1.5; y += Math.sin(a) * r * 1.5;
    }
  }
  // pale winding roads
  g.globalAlpha = 0.85; g.strokeStyle = '#cfc9b0'; g.lineWidth = 2.4;
  for (let i = 0; i < 4; i++) {
    let wx = rrange(-500, 500), wz = -80 - rand() * 100;
    let [x, y] = worldToCanvas(wx, wz, S);
    let a = rrange(-0.6, 0.6) + (wx > 0 ? Math.PI * 0.75 : Math.PI * 0.25);
    g.beginPath(); g.moveTo(x, y);
    for (let sgm = 0; sgm < 40; sgm++) {
      a += rrange(-0.3, 0.3);
      x += Math.cos(a) * 14; y -= Math.sin(a) * 10;
      g.lineTo(x, y);
    }
    g.stroke();
  }
  // lush green around the vineyard hill itself
  const [vx, vy] = worldToCanvas(0, -12, S);
  const gr = g.createRadialGradient(vx, vy, 0, vx, vy, 115 * px);
  gr.addColorStop(0, 'rgba(86,126,56,1)');
  gr.addColorStop(0.6, 'rgba(90,130,60,0.9)');
  gr.addColorStop(1, 'rgba(90,130,60,0)');
  g.globalAlpha = 1; g.fillStyle = gr;
  g.fillRect(vx - 130 * px, vy - 130 * px, 260 * px, 260 * px);
  // subtle mottling on the near grass
  for (let i = 0; i < 900; i++) {
    const wx = rrange(-60, 60), wz = rrange(-130, 40);
    const [x, y] = worldToCanvas(wx, wz, S);
    g.fillStyle = rand() < 0.5 ? 'rgba(70,110,45,0.25)' : 'rgba(130,160,80,0.22)';
    g.beginPath(); g.arc(x, y, rrange(1, 4), 0, Math.PI * 2); g.fill();
  }
  // brighter worn aisles between the rows
  g.globalAlpha = 0.35; g.fillStyle = '#69984a';
  for (let k = -7; k <= 7; k++) {
    const ax = k * 2.8;
    const [x0, y0] = worldToCanvas(ax - 0.75, 22, S);
    const [x1, y1] = worldToCanvas(ax + 0.75, -72, S);
    g.fillRect(x0, y0, x1 - x0, y1 - y0);
  }
  // the vine block continues down the slope as painted stripes
  g.globalAlpha = 0.4; g.strokeStyle = '#4e7038'; g.lineWidth = 1.6;
  for (let k = -8; k <= 8; k++) {
    const rx = k * 2.8;
    const [x0, y0] = worldToCanvas(rx, -72, S);
    const [x1, y1] = worldToCanvas(rx + k * 0.4, -160, S);
    g.beginPath(); g.moveTo(x0, y0); g.lineTo(x1, y1); g.stroke();
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = renderer.capabilities.getMaxAnisotropy();
  return t;
})();

/* ============================================================ terrain mesh */
{
  const geo = new THREE.PlaneGeometry(WORLD, WORLD, 230, 230);
  geo.rotateX(-Math.PI / 2);
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    pos.setY(i, terrainH(pos.getX(i), pos.getZ(i)));
  }
  geo.computeVertexNormals();
  const mat = enhance(new THREE.MeshStandardMaterial({ map: groundTex, roughness: 1, metalness: 0 }));
  const terrain = new THREE.Mesh(geo, mat);
  terrain.receiveShadow = true;
  scene.add(terrain);
}

/* ============================================================ vineyard layout */
const ROWS = [];
for (let k = 0; k < 8; k++) { ROWS.push(1.4 + k * 2.8); ROWS.push(-(1.4 + k * 2.8)); }
const ZMIN = -72, ZMAX = 22, POST_STEP = 4.4;

const dummy = new THREE.Object3D();
const tmpColor = new THREE.Color();

/* ---------- concrete posts ---------- */
{
  const perRow = Math.floor((ZMAX - ZMIN) / POST_STEP) + 1;
  const geo = new THREE.BoxGeometry(0.11, 2.15, 0.14);
  const mat = enhance(new THREE.MeshStandardMaterial({ color: 0xd8d5ca, roughness: 0.9 }));
  const mesh = new THREE.InstancedMesh(geo, mat, ROWS.length * perRow);
  let i = 0;
  for (const rx of ROWS) {
    for (let p = 0; p < perRow; p++) {
      const z = ZMIN + p * POST_STEP + rrange(-0.15, 0.15);
      const x = rx + rrange(-0.05, 0.05);
      dummy.position.set(x, terrainH(x, z) + 1.02, z);
      dummy.rotation.set(rrange(-0.035, 0.035), rrange(0, Math.PI), rrange(-0.05, 0.05));
      dummy.scale.setScalar(rrange(0.93, 1.05));
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
      tmpColor.setHSL(0.12, 0.05, rrange(0.78, 0.9));
      mesh.setColorAt(i, tmpColor);
      i++;
    }
  }
  mesh.castShadow = !TOUCH; mesh.receiveShadow = true; mesh.frustumCulled = false;
  scene.add(mesh);
}

/* ---------- wires (with a hint of sag) ---------- */
{
  const heights = [0.82, 1.32, 1.76];
  const pts = [];
  for (const rx of ROWS) {
    for (const hy of heights) {
      for (let z = ZMIN; z < ZMAX - POST_STEP * 0.5; z += POST_STEP) {
        const z2 = z + POST_STEP, zm = z + POST_STEP / 2;
        const y0 = terrainH(rx, z) + hy, y1 = terrainH(rx, z2) + hy;
        const ym = (y0 + y1) / 2 - 0.035;
        pts.push(rx, y0, z, rx, ym, zm, rx, ym, zm, rx, y1, z2);
      }
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
  const wires = new THREE.LineSegments(geo, new THREE.LineBasicMaterial({ color: 0xb2b0a6, transparent: true, opacity: 0.8 }));
  wires.frustumCulled = false;
  scene.add(wires);
}

/* ---------- gnarled trunks with cordon arms ---------- */
{
  // Two InstancedMeshes — avoids jsm BufferGeometry addon (bare "three" import breaks on Shopify)
  const trunkGeo = new THREE.CylinderGeometry(0.045, 0.09, 0.85, 5);
  trunkGeo.translate(0, 0.42, 0);
  const armGeo = new THREE.CylinderGeometry(0.02, 0.02, 1.5, 4);
  armGeo.rotateX(Math.PI / 2);
  armGeo.translate(0, 0.85, 0);
  const mat = enhance(new THREE.MeshStandardMaterial({ color: 0x54402e, roughness: 1 }));
  const spots = [];
  for (const rx of ROWS) {
    for (let z = ZMIN + 1; z < ZMAX; z += POST_STEP / 2) {
      spots.push([rx + rrange(-0.1, 0.1), z + rrange(-0.6, 0.6)]);
    }
  }
  const trunks = new THREE.InstancedMesh(trunkGeo, mat, spots.length);
  const arms = new THREE.InstancedMesh(armGeo, mat, spots.length);
  spots.forEach(([x, z], i) => {
    dummy.position.set(x, terrainH(x, z), z);
    dummy.rotation.set(rrange(-0.07, 0.07), rrange(-0.2, 0.2), rrange(-0.07, 0.07));
    dummy.scale.set(rrange(0.8, 1.3), rrange(0.85, 1.1), rrange(0.8, 1.3));
    dummy.updateMatrix();
    trunks.setMatrixAt(i, dummy.matrix);
    arms.setMatrixAt(i, dummy.matrix);
    tmpColor.setHSL(0.07, rrange(0.18, 0.32), rrange(0.2, 0.3));
    trunks.setColorAt(i, tmpColor);
    arms.setColorAt(i, tmpColor);
  });
  trunks.castShadow = !TOUCH; trunks.frustumCulled = false;
  arms.castShadow = !TOUCH; arms.frustumCulled = false;
  scene.add(trunks, arms);
}

/* ---------- autumn canopy (instanced leaves) ---------- */
const leafTex = (() => {
  const S = 64, c = document.createElement('canvas'); c.width = c.height = S;
  const g = c.getContext('2d');
  g.clearRect(0, 0, S, S);
  g.fillStyle = '#fff';
  // three-lobed vine leaf silhouette
  g.beginPath();
  g.moveTo(32, 60);
  g.bezierCurveTo(20, 52, 4, 46, 6, 30);
  g.bezierCurveTo(8, 18, 18, 16, 24, 20);
  g.bezierCurveTo(22, 8, 40, 2, 42, 14);
  g.bezierCurveTo(52, 10, 62, 22, 56, 32);
  g.bezierCurveTo(62, 44, 44, 54, 32, 60);
  g.closePath(); g.fill();
  const t = new THREE.CanvasTexture(c);
  return t;
})();

{
  const COUNT = TOUCH ? 10000 : 34000;
  const geo = new THREE.PlaneGeometry(0.2, 0.2);
  const mat = enhance(new THREE.MeshStandardMaterial({
    map: leafTex, alphaTest: 0.45, side: THREE.DoubleSide, roughness: 0.85, color: 0xffffff,
    emissive: 0x1a2008 // keep shadowed backsides from going black — canopy glow
  }), { sway: 0.05, swayMode: 'leaf' });
  const mesh = new THREE.InstancedMesh(geo, mat, COUNT);

  // near rows get the leaf budget
  const weights = ROWS.map(rx => 1 / (1 + Math.abs(rx) * 0.16));
  const totalW = weights.reduce((a, b) => a + b, 0);

  const greens = [0x628f38, 0x74a648, 0x6a963c, 0x82b350, 0x5a8836, 0x8fae4e];
  const golds = [0xe4bd42, 0xd8a832, 0xf0cc52, 0xe0b23a, 0xc99c30, 0xf2d668];

  let i = 0;
  ROWS.forEach((rx, ri) => {
    const n = Math.floor(COUNT * weights[ri] / totalW);
    for (let l = 0; l < n && i < COUNT; l++) {
      const z = rrange(ZMIN, ZMAX);
      // clumpy canopy with sky gaps, like the reference frames
      if (fbm(rx * 0.9 + 40, z * 0.22) < 0.32 && rand() < 0.75) continue;
      const x = rx + rrange(-0.5, 0.5) * rrange(0.4, 1);
      const base = terrainH(rx, z);
      const y = base + 0.85 + Math.pow(rand(), 0.42) * 1.5;
      dummy.position.set(x, y, z);
      // mostly face outward/up so they catch the light, with some scatter
      dummy.rotation.set(rrange(-0.8, 0.8), rrange(0, Math.PI), rrange(-0.6, 0.6));
      dummy.scale.setScalar(rrange(0.7, 1.5));
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
      const hgt = (y - base - 0.85) / 1.5;
      const goldiness = fbm(x * 0.31 + 9, z * 0.31 + 4) * 0.85 + rand() * 0.35 + hgt * 0.22;
      const pal = goldiness > 0.78 ? golds : greens;
      tmpColor.setHex(pal[(rand() * pal.length) | 0]);
      tmpColor.offsetHSL(rrange(-0.012, 0.012), rrange(-0.05, 0.05), rrange(-0.05, 0.06));
      mesh.setColorAt(i, tmpColor);
      i++;
    }
  });
  mesh.count = i;
  mesh.castShadow = !TOUCH; mesh.frustumCulled = false;
  scene.add(mesh);
}

/* ---------- grape clusters (Nebbiolo bunches v2) ----------
   Modeled on reference photos of Nebbiolo at harvest:
   · LARGE conical bunches (~0.35 m long), widest just below the shoulders,
     tapering to a pointed tail — silhouette reads at a glance
   · berries medium-small but packed shoulder-to-shoulder (rings sized so
     berries touch), alternate rings offset half a step like real berries
   · dark purple-black berries under a pale blue-grey waxy bloom:
     ~30% of berries carry the pale dusty coat, rest stay dark; matte finish
   · bunches hang on the AISLE FACE of the rows (pushed outboard), in loose
     groups of 1-3 per vine spot, peduncle rising back into the canopy */
{
  const VINE_SPOTS = TOUCH ? 150 : 420;      // vine spots that carry fruit
  const berryR = 0.026;                      // medium-small berry
  const berryD = berryR * 2;
  const MAX_BUNCHES = TOUCH ? 260 : 700;

  const berryGeo = new THREE.IcosahedronGeometry(berryR, 1);
  const berryMat = enhance(new THREE.MeshStandardMaterial({
    color: 0xffffff, roughness: 0.88, metalness: 0.02,
    emissive: 0x0d0a16 // keeps shadowed berries from crushing to black
  }));
  // peduncle: rises UP from the bunch top into the canopy
  const stalkGeo = new THREE.CylinderGeometry(0.008, 0.013, 0.3, 5);
  stalkGeo.translate(0, 0.15, 0);
  const stalkMat = enhance(new THREE.MeshStandardMaterial({ color: 0x6b543a, roughness: 1 }));

  const mesh = new THREE.InstancedMesh(berryGeo, berryMat, VINE_SPOTS * 3 * 42);
  const meshB = new THREE.InstancedMesh(stalkGeo, stalkMat, VINE_SPOTS * 3);

  // bunch profile: relative ring radius per layer (top shoulder -> tail)
  const PROF = [0.5, 0.8, 0.95, 1.0, 0.92, 0.7, 0.42];
  const R_MAX = 0.072;                       // bunch half-width (m) -> ~14.5cm wide
  const LAYER_STEP = 0.05;                   // tight vertical packing

  let i = 0;
  let sb = 0; // separate stalk index
  const placeBunch = (x, z, hangY, tiltX, yaw) => {
    if (i >= mesh.count - 60 || sb >= meshB.count) return;
    // peduncle up into the canopy
    dummy.position.set(x, hangY, z);
    dummy.rotation.set(tiltX, yaw, 0);
    dummy.scale.setScalar(rrange(0.9, 1.15));
    dummy.updateMatrix();
    meshB.setMatrixAt(sb, dummy.matrix);
    sb++;

    // berries: conical stack of rings, alternating angular offset
    for (let L = 0; L < PROF.length; L++) {
      const r = PROF[L] * R_MAX * rrange(0.92, 1.05);
      const y = hangY - L * LAYER_STEP;
      const n = Math.max(2, Math.floor((Math.PI * 2 * r) / berryD));
      const aOff = (L % 2) * (Math.PI / n);  // offset alternate rings (hex packing)
      for (let k = 0; k < n; k++) {
        if (i >= mesh.count) return;
        const ang = (k / n) * Math.PI * 2 + aOff + rrange(-0.06, 0.06);
        // a few gaps for a natural ragged edge
        if (rand() < 0.1) continue;
        dummy.position.set(
          x + Math.cos(ang) * r,
          y + rrange(-0.008, 0.008),
          z + Math.sin(ang) * r
        );
        dummy.rotation.set(rrange(-0.6, 0.6), rrange(0, Math.PI), rrange(-0.6, 0.6));
        const s = rrange(0.85, 1.15);
        dummy.scale.set(s, s * rrange(0.95, 1.08), s);
        dummy.updateMatrix();
        mesh.setMatrixAt(i, dummy.matrix);
        // Nebbiolo color: dark purple-black + pale bloom mix
        if (rand() < 0.3) {
          tmpColor.setHSL(0.72 + rrange(-0.02, 0.03), rrange(0.08, 0.16), rrange(0.3, 0.42)); // bloom coat
        } else {
          tmpColor.setHSL(0.74 + rrange(-0.02, 0.03), rrange(0.28, 0.42), rrange(0.1, 0.17)); // dark skin
        }
        mesh.setColorAt(i, tmpColor);
        i++;
      }
    }
  };

  // vines with fruit: spaced along the rows, bunches in groups of 1-3
  for (let v = 0; v < VINE_SPOTS; v++) {
    const rx = ROWS[(rand() * ROWS.length) | 0];
    const z = rrange(ZMIN + 1, ZMAX - 1);
    const nBunches = 1 + (rand() * 3 | 0);   // 1-3 bunches per spot
    for (let b = 0; b < nBunches; b++) {
      // hang on the aisle face: push outboard of the row center
      const side = rand() < 0.5 ? -1 : 1;
      const x = rx + side * rrange(0.22, 0.5);
      const zz = z + rrange(-0.3, 0.3);
      const hangY = terrainH(x, zz) + rrange(0.85, 1.15);
      placeBunch(x, zz, hangY, rrange(-0.18, 0.06), rrange(0, Math.PI * 2));
    }
  }
  mesh.count = i;
  meshB.count = sb;
  mesh.frustumCulled = false;
  meshB.frustumCulled = false;
  scene.add(mesh, meshB);
}

/* ---------- grass aisle ---------- */
{
  const COUNT = TOUCH ? 6500 : 24000;
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute([
    -0.02, 0, 0,   0.02, 0, 0,   -0.011, 0.21, 0.012,
     0.011, 0.21, 0.012,   0, 0.34, 0.035
  ], 3));
  geo.setIndex([0, 1, 2, 1, 3, 2, 2, 3, 4]);
  geo.computeVertexNormals();
  const mat = enhance(new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 1, side: THREE.DoubleSide }),
    { sway: 0.06, swayMode: 'grass' });
  const mesh = new THREE.InstancedMesh(geo, mat, COUNT);
  for (let i = 0; i < COUNT; i++) {
    let x, z;
    if (rand() < 0.42) { // the hero aisle stays lush
      x = rrange(-1.35, 1.35); z = rrange(-46, 21);
    } else {
      x = rrange(-22, 22); z = rrange(ZMIN, ZMAX);
    }
    dummy.position.set(x, terrainH(x, z), z);
    dummy.rotation.set(rrange(-0.15, 0.15), rrange(0, Math.PI), rrange(-0.15, 0.15));
    dummy.scale.set(rrange(0.8, 1.4), rrange(0.6, 1.4), rrange(0.8, 1.4));
    dummy.updateMatrix();
    mesh.setMatrixAt(i, dummy.matrix);
    if (rand() < 0.11) tmpColor.setHSL(0.15, rrange(0.28, 0.4), rrange(0.34, 0.44)); // dry blades
    else tmpColor.setHSL(0.25 + rrange(-0.02, 0.025), rrange(0.32, 0.5), rrange(0.2, 0.33));
    mesh.setColorAt(i, tmpColor);
  }
  mesh.frustumCulled = false;
  scene.add(mesh);
}

/* ---------- fallen leaves under the rows ---------- */
{
  const COUNT = TOUCH ? 900 : 2600;
  const geo = new THREE.PlaneGeometry(0.15, 0.15);
  const mat = enhance(new THREE.MeshStandardMaterial({
    map: leafTex, alphaTest: 0.45, side: THREE.DoubleSide, roughness: 1
  }));
  const mesh = new THREE.InstancedMesh(geo, mat, COUNT);
  const browns = [0xb08c34, 0x9c7a2c, 0xc4a144, 0x8a6d28, 0xd0ab48];
  for (let i = 0; i < COUNT; i++) {
    const rx = ROWS[(rand() * ROWS.length) | 0];
    const x = rx + rrange(-1.1, 1.1);
    const z = rrange(ZMIN, ZMAX);
    dummy.position.set(x, terrainH(x, z) + 0.015, z);
    dummy.rotation.set(-Math.PI / 2 + rrange(-0.35, 0.35), 0, rrange(0, Math.PI * 2));
    dummy.scale.setScalar(rrange(0.6, 1.2));
    dummy.updateMatrix();
    mesh.setMatrixAt(i, dummy.matrix);
    tmpColor.setHex(browns[(rand() * browns.length) | 0]);
    tmpColor.offsetHSL(0, 0, rrange(-0.06, 0.06));
    mesh.setColorAt(i, tmpColor);
  }
  mesh.frustumCulled = false;
  scene.add(mesh);
}

/* ============================================================ valley dressing */
/* ---------- trees & hedgerows ---------- */
{
  const COUNT = TOUCH ? 420 : 950;
  const blobGeo = new THREE.IcosahedronGeometry(1, 1);
  const blobMat = enhance(new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 1 }));
  const blobs = new THREE.InstancedMesh(blobGeo, blobMat, COUNT);

  const spots = [];
  const addSpot = (x, z) => {
    if (Math.abs(x) < 30 && z > -85 && z < 32) return;
    if (Math.hypot(x, z) > 640) return;
    spots.push([x, z]);
  };
  // hedgerow lines
  for (let l = 0; l < 90 && spots.length < COUNT; l++) {
    let x = rrange(-560, 560), z = rrange(-620, 60);
    const a = rrange(0, Math.PI * 2), n = 4 + (rand() * 6 | 0);
    for (let b = 0; b < n; b++) { addSpot(x, z); x += Math.cos(a) * rrange(3, 6); z += Math.sin(a) * rrange(3, 6); }
  }
  while (spots.length < COUNT) addSpot(rrange(-620, 620), rrange(-640, 70));

  spots.length = COUNT;
  spots.forEach(([x, z], i) => {
    const s = rrange(1.4, 3.6);
    dummy.position.set(x, terrainH(x, z) + s * 0.72, z);
    dummy.rotation.set(0, rrange(0, Math.PI), 0);
    dummy.scale.set(s * rrange(0.85, 1.25), s * rrange(0.75, 1.15), s * rrange(0.85, 1.25));
    dummy.updateMatrix();
    blobs.setMatrixAt(i, dummy.matrix);
    tmpColor.setHSL(0.28 + rrange(-0.03, 0.02), rrange(0.28, 0.42), rrange(0.16, 0.26));
    blobs.setColorAt(i, tmpColor);
  });
  blobs.frustumCulled = false;
  scene.add(blobs);

  // cypress accents
  const N2 = TOUCH ? 60 : 140;
  const cyGeo = new THREE.ConeGeometry(0.8, 4.4, 6);
  cyGeo.translate(0, 2.2, 0);
  const cy = new THREE.InstancedMesh(cyGeo, enhance(new THREE.MeshStandardMaterial({ color: 0x2e4527, roughness: 1 })), N2);
  for (let i = 0; i < N2; i++) {
    let x, z;
    do { x = rrange(-540, 540); z = rrange(-560, 40); } while (Math.abs(x) < 30 && z > -85 && z < 32);
    dummy.position.set(x, terrainH(x, z), z);
    dummy.rotation.set(0, rrange(0, Math.PI), rrange(-0.03, 0.03));
    dummy.scale.set(rrange(0.7, 1.2), rrange(0.8, 1.6), rrange(0.7, 1.2));
    dummy.updateMatrix();
    cy.setMatrixAt(i, dummy.matrix);
  }
  cy.frustumCulled = false;
  scene.add(cy);
}

/* ---------- Castello di Serralunga d'Alba — the photo skyline, far on its own ridge ----------
   Faithful stylization of the real complex (three vertical masses on a spur):
   · LEFT: big cylindrical torrione, slightly shorter than the keep, forked merlons on top
   · MIDDLE: tall rectangular palacium (tallest volume), crowned with forked merlons,
     gabled roof barely peeking behind the crown
   · RIGHT: second square tower volume, nearly keep-height, merloned as well
   · pale pink-tan Langhe stone; annex wing + low court wall at the base
   · village of narrow stone houses with tiled roofs climbing the spur below,
     church bell tower standing apart on the left */
{
  const group = new THREE.Group();
  // warm BRICK Langhe castle (per photo: terracotta red-brown, not pale stone)
  const stonePale = new THREE.MeshStandardMaterial({ color: 0xb5764a, roughness: 0.95, metalness: 0.02 });
  const stoneGrey = new THREE.MeshStandardMaterial({ color: 0xa8754e, roughness: 0.96 });
  const stoneEdge = new THREE.MeshStandardMaterial({ color: 0x8f5c39, roughness: 0.97 }); // darker quoins
  const brick = new THREE.MeshStandardMaterial({ color: 0x8e4531, roughness: 0.95 });
  const brickDark = new THREE.MeshStandardMaterial({ color: 0x753a2b, roughness: 0.95 });
  const roofTerra = new THREE.MeshStandardMaterial({ color: 0x9a5238, roughness: 0.9 });
  const roofDark = new THREE.MeshStandardMaterial({ color: 0x7a4030, roughness: 0.92 });
  const ochre = new THREE.MeshStandardMaterial({ color: 0xd6b688, roughness: 0.95 });
  const cream = new THREE.MeshStandardMaterial({ color: 0xe2d6bc, roughness: 0.95 });
  const cypress = new THREE.MeshStandardMaterial({ color: 0x2a3f28, roughness: 1 });


  // ---- swallow-tail (Ghibelline) merlon: a forked notch at the top ----
  function ghibMerlon(w, h, d, mat) {
    const notch = h * 0.42;           // fork depth
    const baseH = h - notch;
    const mesh = new THREE.Group();
    const base = new THREE.Mesh(new THREE.BoxGeometry(w, baseH, d), mat);
    base.position.y = baseH / 2;
    const pw = w * 0.34;              // prong width
    const px = w * 0.33;              // prong inset from center
    const prongL = new THREE.Mesh(new THREE.BoxGeometry(pw, notch, d), mat);
    prongL.position.set(-px, baseH + notch / 2, 0);
    const prongR = new THREE.Mesh(new THREE.BoxGeometry(pw, notch, d), mat);
    prongR.position.set(px, baseH + notch / 2, 0);
    mesh.add(base, prongL, prongR);
    return mesh;
  }
  // merlon strip around a RECTANGULAR wall top: forked merlons ringing
  // both long faces and both short faces (a true cammino di ronda)
  function forkedCrown(parent, cx, top, cz, w, d, count, mat) {
    const perLong = Math.max(6, Math.round(count * (w / (w + d))));
    const perShort = Math.max(4, count - perLong);
    const merlonW = Math.max(0.4, w / (perLong * 2.1));
    const merlonD = Math.max(0.4, d / (perShort * 2.1));
    const ring = (n, span, fixed, axis) => {
      for (let i = 0; i < n; i++) {
        const off = -span / 2 + i * (span / n) + span / (n * 2);
        for (const side of [-1, 1]) {
          const m = axis === 'x'
            ? ghibMerlon(Math.min(merlonW, 0.8), 1.15, 0.6, mat)
            : ghibMerlon(Math.min(merlonD, 0.8), 1.15, 0.6, mat);
          if (axis === 'x') m.position.set(cx + off, top, fixed + side * (d / 2));
          else m.position.set(fixed + side * (w / 2), top, cz + off);
          parent.add(m);
        }
      }
    };
    ring(perLong, w - 1.2, cz, 'x');   // long faces (front/back)
    ring(perShort, d - 1.2, cx, 'z');  // short faces (left/right)
  }
  const boxAt = (parent, w, h, d, mat, x, y, z) => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
    m.position.set(x, y, z);
    parent.add(m); return m;
  };

  const CX = -100, CZ = -418;
  const castle = new THREE.Group();
  castle.position.set(CX, terrainH(CX, CZ), CZ);
  castle.rotation.y = 0.26; // show a corner, not a flat face

  /* ---- MIDDLE mass: tall SLIM palacium (tallest) ---- */
  const keepW = 7.0, keepD = 6.0, keepH = 30;
  boxAt(castle, keepW, keepH, keepD, stonePale, 0, keepH / 2, 0);
  // darker quoins at the corners
  for (const [sx, sz] of [[-keepW / 2 + 0.35, -keepD / 2 + 0.45], [keepW / 2 - 0.45, -keepD / 2 + 0.45], [-keepW / 2 + 0.45, keepD / 2 - 0.45], [keepW / 2 - 0.45, keepD / 2 - 0.45]]) {
    boxAt(castle, 0.9, keepH, 0.9, stoneEdge, sx, keepH / 2, sz);
  }
  // gabled roof tucked BEHIND the crown: narrow ridge just above the walk
  const gableRoof = new THREE.Mesh(new THREE.ConeGeometry(keepD * 0.78, 1.8, 4), roofDark);
  gableRoof.position.set(0, keepH + 1.0, 0);
  gableRoof.rotation.y = Math.PI / 4;
  gableRoof.scale.set(0.55, 1, 1.65); // narrow across, long ridge along the long axis
  castle.add(gableRoof);
  // parapet band under the merlons so the crown reads as a solid walkway ring
  boxAt(castle, keepW + 0.5, 0.55, keepD + 0.5, stoneGrey, 0, keepH - 0.2, 0);
  // forked merlons ringing the walk
  forkedCrown(castle, 0, keepH + 0.35, 0, keepW, keepD, 9, stonePale);

  // windows: sparse Gothic single-lancet arched windows on noble floors,
  // tiny square openings lower down, larger windows on the top great-hall floor
  // long faces (x = ±keepW/2)
  for (const sx of [-1, 1]) {
    const wx = sx * (keepW / 2 + 0.06);
    // noble floors (3 floors): 3 lancets each face
    for (let f = 0; f < 3; f++) {
      const y = 5.5 + f * 6.2;
      for (let k = -1; k <= 1; k++) {
        const wz = k === 0 ? 0 : k * 3.6;
        const wnd = new THREE.Mesh(new THREE.BoxGeometry(0.9, 1.7, 0.5), stoneEdge);
        wnd.position.set(wx, y, wz);
        castle.add(wnd);
      }
    }
    // tiny square openings lower down
    for (let k = -2; k <= 2; k++) {
      const wnd = new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.6, 0.4), stoneEdge);
      wnd.position.set(wx, 2.4, k * 2.1);
      castle.add(wnd);
    }
    // great-hall floor: larger windows
    for (let k = -1; k <= 1; k++) {
      const wnd = new THREE.Mesh(new THREE.BoxGeometry(1.5, 2.4, 0.5), stoneEdge);
      wnd.position.set(wx, keepH - 2.8, k * 4.2);
      castle.add(wnd);
    }
  }
  // short faces (z = ±keepD/2) — a couple of lancets
  for (const sz of [-1, 1]) {
    const wz = sz * (keepD / 2 + 0.06);
    for (let f = 0; f < 2; f++) {
      const y = 6.5 + f * 6.4;
      const wnd = new THREE.Mesh(new THREE.BoxGeometry(0.8, 1.6, 0.45), stoneEdge);
      wnd.position.set(1.5, y, wz);
      castle.add(wnd);
    }
  }

  /* ---- LEFT mass: cylindrical torrione, attached at the corner ---- */
  const torR = 2.9, torH = 24.5;
  const tor = new THREE.Mesh(new THREE.CylinderGeometry(torR, torR * 1.1, torH, 16), stonePale);
  tor.position.set(-keepW / 2 - torR * 0.45, torH / 2, 0.3);
  castle.add(tor);
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const m = ghibMerlon(0.7, 1.15, 0.7, stonePale);
    m.position.set(tor.position.x + Math.cos(a) * torR, torH + 0.25, tor.position.z + Math.sin(a) * torR);
    castle.add(m);
  }
  // a few slit windows on the torrione
  for (let f = 0; f < 4; f++) {
    const wnd = new THREE.Mesh(new THREE.BoxGeometry(0.35, 1.2, 0.4), stoneEdge);
    wnd.position.set(tor.position.x - torR - 0.02, 4 + f * 5.2, tor.position.z);
    castle.add(wnd);
  }

  /* ---- RIGHT mass: lower square volume (subordinate, not a competing tower) ---- */
  const sqW = 6.0, sqH = 16.0;
  boxAt(castle, sqW, sqH, sqW, stonePale, keepW / 2 + sqW / 2 - 0.6, sqH / 2, 0.3);
  // square tower crown: forked merlons on all four faces
  forkedCrown(castle, keepW / 2 + sqW / 2 - 0.8, sqH + 0.35, 0.4, sqW, sqW, 7, stonePale);
  // a few windows on the square tower
  for (let f = 0; f < 3; f++) {
    boxAt(castle, 1.1, 1.8, 0.45, stoneEdge, keepW / 2 + sqW / 2 + 0.02, 5 + f * 6.6, 0.4);
  }

  /* ---- lower annex wing leaning at the base ---- */
  boxAt(castle, 8, 8.5, 8, stoneGrey, -keepW / 2 + 3, 4.2, keepD / 2 + 2.5);
  // annex roof
  const annexRoof = new THREE.Mesh(new THREE.ConeGeometry(5.6, 2.2, 4), roofTerra);
  annexRoof.position.set(-keepW / 2 + 3, 9.4, keepD / 2 + 2.5);
  annexRoof.rotation.y = Math.PI / 4;
  annexRoof.scale.z = 1.5;
  castle.add(annexRoof);
  // a small annex window
  boxAt(castle, 0.7, 1.1, 0.4, stoneEdge, -keepW / 2 + 1.5, 5, keepD / 2 + 1);

  group.add(castle);

  /* ---- low court wall hugging the rock line, with small merlons ---- */
  {
    const wallMat = stoneGrey;
    const wl = [ -9.5, -6, -2, 2, 6, 9.5 ];   // x offsets along the front
    const wd = 0.8, wh = 3.2;
    // front wall (toward the vineyard, -z side) as a series of segments
    for (const wx of wl) {
      const m = boxAt(group, 4.2, wh, wd, wallMat, CX + wx, terrainH(CX + wx, CZ - 9) + wh / 2 - 1.2, CZ - 9);
      for (let k = -1; k <= 1; k++) {
        const mm = ghibMerlon(0.6, 0.85, 0.6, stonePale);
        mm.position.set(CX + wx + k * 1.3, m.position.y + wh / 2 + 0.42, CZ - 9);
        group.add(mm);
      }
    }
    // side segment running back toward the keep on the left
    const s1 = boxAt(group, wd, wh, 12, wallMat, CX - 11.5, terrainH(CX - 11, CZ - 3) + wh / 2 - 1.0, CZ - 3);
    for (let k = -2; k <= 2; k++) {
      const mm = ghibMerlon(0.6, 0.85, 0.6, stonePale);
      mm.position.set(CX - 11, s1.position.y + wh / 2 + 0.42, CZ - 3 + k * 2.4);
      group.add(mm);
    }
  }

  /* --- 2. CHURCH #1 — in the town cluster, campanile clearly lower than the keep --- */
  const ch1 = new THREE.Group();
  ch1.position.set(-112.5, terrainH(-112.5, -420), -420);
  ch1.rotation.y = 0.2;
  boxAt(ch1, 6, 6, 9, cream, -1.5, 3, 0);
  const nave1Roof = new THREE.Mesh(new THREE.ConeGeometry(4.4, 2.4, 4), roofTerra);
  nave1Roof.position.set(-1.5, 7.2, 0);
  nave1Roof.rotation.y = Math.PI / 4;
  nave1Roof.scale.z = 1.6;
  ch1.add(nave1Roof);
  boxAt(ch1, 2.9, 15, 2.9, stonePale, 2.5, 7.5, -1.5);
  const cap1 = new THREE.Mesh(new THREE.ConeGeometry(2.3, 3, 4), roofDark);
  cap1.position.set(2.5, 16.5, -1.5);
  cap1.rotation.y = Math.PI / 4;
  ch1.add(cap1);
  group.add(ch1);

  /* --- 3. CHURCH #2 — farther LEFT along the ridge, past a gap of trees:
         dark red brick, gabled nave, tall slender campanile with pointed spire --- */
  const ch2 = new THREE.Group();
  ch2.position.set(-140, terrainH(-140, -424), -424);
  ch2.rotation.y = 0.15;
  boxAt(ch2, 7, 7, 13, brick, 0, 3.5, 0);
  const gable = new THREE.Mesh(new THREE.ConeGeometry(5.2, 3, 4), roofDark);
  gable.position.set(0, 8.4, 0);
  gable.rotation.y = Math.PI / 4;
  gable.scale.z = 2.1; // stretched pyramid reads as the gabled nave roof
  ch2.add(gable);
  boxAt(ch2, 4, 5, 3.4, brickDark, 0, 2.5, 7.6); // apse
  boxAt(ch2, 2.6, 18, 2.6, brickDark, 5.1, 9, -4.2);
  const spire2 = new THREE.Mesh(new THREE.ConeGeometry(2.15, 5, 8), roofDark);
  spire2.position.set(5.1, 20.4, -4.2);
  ch2.add(spire2);
  group.add(ch2);

  /* --- 4. cream/ochre houses, terracotta roofs, cascading down the sunny slope --- */
  function house(x, z, w, d, h, yawH, wallMat) {
    const y = terrainH(x, z);
    const walls = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), wallMat);
    walls.position.set(x, y + h / 2, z);
    walls.rotation.y = yawH;
    const roof = new THREE.Mesh(new THREE.ConeGeometry(Math.max(w, d) * 0.72, h * 0.42, 4), roofTerra);
    roof.position.set(x, y + h + h * 0.2, z);
    roof.rotation.y = yawH + Math.PI / 4;
    group.add(walls, roof);
  }
  // dense village cluster hugging the castle + church #1 — placed to avoid the court wall
  for (let i = 0; i < 22; i++) {
    const x = rrange(-119, -94), z = rrange(-427, -411);
    if (Math.hypot(x - CX, z - CZ) < 15) continue; // keep the castle + court wall footprint clear
    const wall = (i % 3 === 0) ? ochre : (i % 3 === 1) ? cream : stoneGrey;
    house(x, z, rrange(4, 7.5), rrange(4.5, 8), rrange(3.8, 7), rrange(-0.3, 0.5), wall);
  }
  // cascade toward the vineyard — stop well short of the vine rows
  for (let i = 0; i < 14; i++) {
    const t = i / 13;
    const x = -109 + rrange(-9, 12) + t * 6;
    const z = -408 + t * 14 + rrange(-3, 3);
    if (z > -385 || Math.abs(x) < 70) continue;
    house(x, z, rrange(4, 7), rrange(4.5, 8), rrange(3.6, 6.2), rrange(-0.3, 0.5), i % 2 ? cream : ochre);
  }

  /* --- trees in the skyline gap between the two churches + flank cypresses --- */
  const blobMat = new THREE.MeshStandardMaterial({ color: 0x33502e, roughness: 1 });
  for (let i = 0; i < 5; i++) {
    const x = rrange(-128, -119), z = rrange(-425, -417);
    const s = rrange(2.6, 4.2);
    const b = new THREE.Mesh(new THREE.IcosahedronGeometry(s, 1), blobMat);
    b.position.set(x, terrainH(x, z) + s * 0.7, z);
    group.add(b);
  }
  for (let i = 0; i < 9; i++) {
    const side = i % 2 ? 1 : -1;
    const x = -102 + side * rrange(26, 52) + rrange(-6, 6);
    const z = -418 + rrange(-14, 40);
    const c = new THREE.Mesh(new THREE.ConeGeometry(1.2, rrange(8, 13), 6), cypress);
    c.position.set(x, terrainH(x, z) + 4, z);
    group.add(c);
  }
  // scattered farmhouses in the valley — keep a wide clear corridor so none sit mid-aisle
  for (let i = 0; i < 7; i++) {
    let x, z;
    do {
      x = rrange(-420, 420);
      z = rrange(-470, -160);
    } while (Math.abs(x) < 85); // never near the vineyard path
    house(x, z, rrange(7, 12), rrange(7, 13), rrange(4.5, 7), rrange(0, Math.PI), stoneGrey);
  }
  scene.add(group);
}

/* ---------- Alps on the horizon — photographic rebuild (Serralunga view) -----
   The real view is ONE continuous serrated snow wall (Monte Rosa massif) with
   no sky gaps: warm ivory sun faces, cool blue-grey shade, dark rock couloirs
   fading downslope. In front: a bluer half-rock main range, then a LOW
   mist-washed band of dark wooded foothills, everything dissolving into
   bright valley fog. Each layer is a ribbon GRID (crest columns x height
   rows) so color can vary along the ridge AND vertically — couloirs are 2D
   gullies, never full-height bars. Crests = overlapping sharpened massifs on
   a high floor (no gaps) + two midpoint-displacement passes. fog:false —
   colors pre-tinted for atmospheric perspective. */
{
  const ARC = 2.6;                                    // radians of horizon spanned
  const ROWS = 12;                                    // height steps per column
  const HAZE = new THREE.Color(0xe9eff6);

  /* deterministic 1D value noise (independent of the global scene seed) */
  const hash1 = (n) => {
    let t = Math.imul(n ^ 0x9e3779b9, 0x85ebca6b);
    t = Math.imul(t ^ (t >>> 13), 0xc2b2ae35);
    t ^= t >>> 16;
    return (t >>> 0) / 4294967295;
  };
  const vnoise = (x) => {
    const i = Math.floor(x), f = x - i, u = f * f * (3 - 2 * f);
    return hash1(i) * (1 - u) + hash1(i + 1) * u;
  };
  const fbm1 = (x) => vnoise(x) * 0.55 + vnoise(x * 2.3 + 11.7) * 0.28 + vnoise(x * 5.1 + 4.9) * 0.17;

  const buildRidge = (R, o) => {
    // local deterministic rng (does not disturb the global scene seed)
    let s = Math.floor(o.seed * 1000) + 7;
    const rnd = () => { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; };

    const rockC = new THREE.Color(o.rock);
    const snowC = new THREE.Color(o.snow);
    const cTmp = new THREE.Color();

    /* --- massifs: overlapping summits on a high floor →
           continuous wall, serrated skyline, no sky gaps --- */
    const bumps = [];
    for (let k = 0; k < o.nMassifs; k++) {
      bumps.push({
        a: -ARC / 2 + ((k + 0.5) / o.nMassifs) * ARC + (rnd() - 0.5) * (ARC / o.nMassifs) * 0.5,
        w: (ARC / o.nMassifs) * (1.18 + rnd() * 0.5),  // broad bodies overlap; no picket fence
        h: o.peakH * (0.72 + rnd() * 0.28),
        summit: 0.28 + rnd() * 0.44,                    // strongly asymmetric summit position
        sharp: 1.08 + rnd() * 0.38,                    // faceted crown without needle spikes
        left: 0.62 + rnd() * 0.28,
        right: 0.68 + rnd() * 0.38,
      });
    }
    const floorH = o.peakH * o.floor;
    const crest = (a) => {
      let h = 0;
      for (const b of bumps) {
        const half = b.w * 0.5;
        const d = (a - b.a) / half;
        if (Math.abs(d) < 1) {
          // Two unequal planes meeting at an offset summit. The broad
          // shoulders overlap into a continuous range, while the summit
          // remains a short, sharp Alpine tooth rather than a dome.
          const q = d < 0 ? (-d / b.summit) : (d / (1 - b.summit));
          const profile = Math.pow(Math.max(0, 1 - q), b.sharp);
          const side = d < 0 ? b.left : b.right;
          h = Math.max(h, b.h * (1 - side * (1 - profile)));
        }
      }
      return Math.max(h, floorH) * (0.84 + 0.24 * fbm1(a * 9.1 + o.seed * 17.9));
    };

    /* --- crest polyline + serration --- */
    const posts = [];
    for (let k = 0; k <= o.posts; k++) {
      const a = -ARC / 2 + (k / o.posts) * ARC;
      posts.push({ a, h: Math.max(3, crest(a)) });
    }
    posts[0].h = 3; posts[o.posts].h = 3;              // tuck the arc ends away
    for (let l = 0; l < 2; l++) {
      const rgh = l === 0 ? o.rough1 : o.rough2;
      const out = [posts[0]];
      for (let i = 0; i < posts.length - 1; i++) {
        const p0 = posts[i], p1 = posts[i + 1];
        const jitter = (rnd() * 2 - 1) * rgh * Math.abs(p1.h - p0.h)
                     + (rnd() * 2 - 1) * o.peakH * 0.012;
        out.push({ a: (p0.a + p1.a) / 2, h: Math.max(3, (p0.h + p1.h) / 2 + jitter) });
        out.push(p1);
      }
      posts.length = 0; posts.push(...out);
    }

    /* per-column shading inputs: crest slope → sun/shade, steepness */
    const da = posts[1].a - posts[0].a;
    const colSteep = posts.map((p, k) => {
      const hPrev = posts[Math.max(0, k - 1)].h, hNext = posts[Math.min(posts.length - 1, k + 1)].h;
      return clamp(Math.abs(hNext - hPrev) / Math.max(2 * da * R, 0.001) / 1.1, 0, 1);
    });
    const colLit = posts.map((p, k) => {
      const hPrev = posts[Math.max(0, k - 1)].h, hNext = posts[Math.min(posts.length - 1, k + 1)].h;
      const grad = (hNext - hPrev) / (0.5 * (hPrev + hNext) + 1);
      return clamp(0.5 + grad * 2.4, 0.04, 0.97);      // faces rising to the sun catch light
    });

    /* --- ribbon grid: column k spans yBase..crest(k) in ROWS steps --- */
    const yBase = -30;
    const pos = [];
    const col = [];
    const vid = (k, m) => (k * (ROWS + 1) + m);
    for (let k = 0; k < posts.length; k++) {
      const p = posts[k];
      const x = Math.sin(p.a) * R, z = -Math.cos(p.a) * R;
      const wob = (vnoise(p.a * 3.7 + o.seed * 21.3) - 0.5) * o.snowWob;
      for (let m = 0; m <= ROWS; m++) {
        const tF = m / ROWS;                            // 0 base → 1 crest
        const y = yBase + tF * (p.h - yBase);
        pos.push(x, y, z);

        // altitude snow on an irregular, noisy snowline
        const snowT = sst(o.snowLo + wob, o.snowHi + wob, y);
        // couloirs: gullies gated per column, strongest near the crest,
        // fading downslope — real gullies never reach the valley floor
        const gully = sst(0.58, 0.78, fbm1(p.a * 18 + o.seed * 47.7))
                    * sst(0.18, 0.62, tF) * sst(0.25, 0.65, colSteep[k]) * snowT;
        // broad rock patches: 2D noise that moves with height (no bars)
        const crag = sst(0.6, 0.78, fbm1(p.a * 9.3 + y * 0.1 + o.seed * 31.1)) * snowT * 0.55;
        const rockT = clamp((1 - snowT) + Math.max(gully, crag), 0, 1);
        cTmp.copy(snowC).lerp(rockC, rockT);
        if (gully > 0) cTmp.multiplyScalar(1 - 0.4 * gully);        // gullies darkest
        // broken light of real crags + sun/shade tint
        const micro = 0.95 + 0.1 * vnoise(p.a * 150 + y * 0.6);
        const lit = colLit[k];
        cTmp.multiplyScalar(micro * lerp(0.4, 1.18, lit));
        cTmp.r *= lerp(0.78, 1.1, lit);                             // shadow: cool, sun: warm
        cTmp.b *= lerp(1.24, 0.95, lit);
        cTmp.multiplyScalar(0.98 + 0.04 * sst(0, o.peakH, y));      // summits catch more sky
        // valley haze: feet dissolve into mist, far layer lifted overall
        const hazeT = clamp(o.hazeMax * (1 - sst(o.hazeLo, o.hazeHi, y))
          + o.hazeHigh * (1 - sst(o.peakH * 0.5, o.peakH, y)), 0, 1);
        cTmp.lerp(HAZE, hazeT);
        col.push(cTmp.r, cTmp.g, cTmp.b);
      }
    }
    /* two triangles per grid cell */
    const idx = [];
    for (let k = 0; k < posts.length - 1; k++) {
      for (let m = 0; m < ROWS; m++) {
        const a = vid(k, m), b = vid(k, m + 1), c = vid(k + 1, m), d = vid(k + 1, m + 1);
        idx.push(a, b, c, b, d, c);
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
    geo.setIndex(idx);
    const mesh = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ vertexColors: true, fog: false, side: THREE.DoubleSide }));
    mesh.frustumCulled = false;
    scene.add(mesh);
  };

  // 1 — far snow wall (Monte Rosa massif): continuous, warm ivory, near-total snow.
  //     Distance recipe (user: "still too close"): push radius out, cut height,
  //     and lift haze together — radius alone barely changes angular size.
  buildRidge(1700, {
    nMassifs: 6, posts: 150, peakH: 215, floor: 0.52, rough1: 0.2, rough2: 0.1, seed: 4.2,
    rock: 0x8fa2b6, snow: 0xfbf4e2, snowLo: 14, snowHi: 70, snowWob: 40,
    hazeMax: 0.9, hazeLo: 3, hazeHi: 24, hazeHigh: 0.12,
  });
  // 2 — main range: half rock, half snow, bluer and a touch darker
  buildRidge(1450, {
    nMassifs: 13, posts: 150, peakH: 150, floor: 0.4, rough1: 0.22, rough2: 0.11, seed: 1.3,
    rock: 0x6f8296, snow: 0xf3f0e4, snowLo: 48, snowHi: 110, snowWob: 45,
    hazeMax: 0.84, hazeLo: 2, hazeHi: 20, hazeHigh: 0.1,
  });
  // 3 — wooded foothill band: LOW, mist-washed blue-grey, snowless, rolling
  buildRidge(1240, {
    nMassifs: 16, posts: 130, peakH: 46, floor: 0.3, rough1: 0.08, rough2: 0.05, seed: 9.4,
    rock: 0x46586a, snow: 0xcdd8e2, snowLo: 62, snowHi: 95, snowWob: 30,
    hazeMax: 0.7, hazeLo: 1, hazeHi: 30, hazeHigh: 0,
  });

  /* soft valley mist — smooth bell curve, no hard band edges */
  {
    const c = document.createElement('canvas'); c.width = 8; c.height = 256;
    const g = c.getContext('2d');
    const img = g.createImageData(8, 256);
    for (let y = 0; y < 256; y++) {
      const t = y / 255;                              // 0 = top of band, 1 = bottom
      const a = 0.5 * Math.exp(-Math.pow((t - 0.55) / 0.28, 2));
      for (let x = 0; x < 8; x++) {
        const o2 = (y * 8 + x) * 4;
        img.data[o2] = 233; img.data[o2 + 1] = 239; img.data[o2 + 2] = 246;
        img.data[o2 + 3] = Math.round(a * 255);
      }
    }
    g.putImageData(img, 0, 0);
    const mist = new THREE.Mesh(
      new THREE.CylinderGeometry(700, 700, 150, 72, 1, true),
      new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(c), transparent: true, fog: false, depthWrite: false, side: THREE.BackSide })
    );
    mist.position.y = 20;
    mist.renderOrder = 5;
    mist.frustumCulled = false;
    scene.add(mist);
  }
}

/* ============================================================ birds */
const flocks = [];
{
  const mat = new THREE.MeshBasicMaterial({ color: 0x27302a, side: THREE.DoubleSide });
  function makeBird(scale) {
    const bird = new THREE.Group();
    const wingGeo = new THREE.BufferGeometry();
    wingGeo.setAttribute('position', new THREE.Float32BufferAttribute([
      0, 0, 0.13,  0, 0, -0.13,  -0.62, 0.06, -0.03
    ], 3));
    wingGeo.setIndex([0, 1, 2]);
    wingGeo.computeVertexNormals();
    const l = new THREE.Mesh(wingGeo, mat);
    const r = new THREE.Mesh(wingGeo, mat);
    r.scale.x = -1;
    bird.add(l, r);
    bird.userData = { l, r };
    bird.scale.setScalar(scale);
    return bird;
  }
  function flock(cx, cz, radius, y, speed, count, scale) {
    const group = new THREE.Group();
    const birds = [];
    for (let i = 0; i < count; i++) {
      const b = makeBird(scale * rrange(0.8, 1.25));
      b.userData.phase = rrange(0, Math.PI * 2);
      b.userData.off = new THREE.Vector3(rrange(-9, 9), rrange(-2.5, 2.5), rrange(-9, 9));
      birds.push(b); group.add(b);
    }
    scene.add(group);
    flocks.push({ cx, cz, radius, y, speed, birds, a0: rrange(0, Math.PI * 2) });
  }
  flock(45, -170, 62, 6, 0.075, 6, 1.5);
  flock(-130, -240, 82, 12, -0.055, 8, 1.7);
  flock(0, -105, 42, 2, 0.095, 4, 1.2);
}
function updateBirds(t) {
  for (const f of flocks) {
    const a = f.a0 + t * f.speed;
    for (const b of f.birds) {
      const aa = a + b.userData.phase * 0.22;
      const px = f.cx + Math.cos(aa) * f.radius + b.userData.off.x;
      const pz = f.cz + Math.sin(aa) * f.radius * 0.72 + b.userData.off.z;
      const py = f.y + b.userData.off.y + Math.sin(t * 0.7 + b.userData.phase) * 1.2;
      b.position.set(px, py, pz);
      b.rotation.y = -aa + (f.speed > 0 ? 0 : Math.PI);
      const flap = Math.sin(t * 8.5 + b.userData.phase * 5) * 0.85;
      b.userData.l.rotation.z = flap;
      b.userData.r.rotation.z = -flap;
    }
  }
}

/* ============================================================ state machine + cinematic */
const body = document.body;
if (TOUCH) body.classList.add('touch');

let state = 'veil'; // veil -> cine -> hold -> walk
let cineStart = 0;
const CINE_DUR = 11.5;
const CINE = { z0: 15, z1: -28, pitch0: -0.045, pitch1: -0.085 };

const veil = document.getElementById('veil');
const holdPanel = document.getElementById('holdPanel');
const keysHint = document.getElementById('keysHint');
document.getElementById('loadState').textContent = 'The vineyard is ready';

document.getElementById('startBtn').addEventListener('click', () => {
  state = 'cine'; cineStart = performance.now();
  veil.classList.add('gone');
  body.classList.add('started', 'cine');
});
document.getElementById('skip').addEventListener('click', () => { if (state === 'cine') endCine(); });

function endCine() {
  state = 'hold';
  applyCine(1);
  body.classList.remove('cine');
  holdPanel.classList.add('show');
}

function applyCine(t) {
  const e = t * t * (3 - 2 * t); // smooth glide, straight down the aisle
  const z = lerp(CINE.z0, CINE.z1, e);
  camera.position.set(0, terrainH(0, z) + 1.82, z);
  camera.rotation.set(lerp(CINE.pitch0, CINE.pitch1, e), 0, 0);
}

/* ============================================================ walk controls */
let yaw = 0, pitch = -0.06;
const keys = {};
let holdWalk = false;
const vel = new THREE.Vector3();
let walkT = 0;

document.getElementById('walkBtn').addEventListener('click', () => {
  state = 'walk';
  holdPanel.classList.remove('show');
  body.classList.add('walking');
  yaw = camera.rotation.y; pitch = camera.rotation.x;
  if (!TOUCH) {
    keysHint.classList.add('show');
    setTimeout(() => keysHint.classList.remove('show'), 7000);
    canvas.requestPointerLock?.();
  }
});

addEventListener('keydown', e => { keys[e.code] = true; });
addEventListener('keyup', e => { keys[e.code] = false; });

canvas.addEventListener('click', () => {
  if (state === 'walk' && !TOUCH && document.pointerLockElement !== canvas) canvas.requestPointerLock?.();
});
addEventListener('mousemove', e => {
  if (state !== 'walk') return;
  if (document.pointerLockElement === canvas) {
    yaw -= e.movementX * 0.0021;
    pitch = clamp(pitch - e.movementY * 0.0021, -1.15, 0.9);
  } else if (dragging) {
    yaw -= (e.clientX - dragX) * 0.0032;
    pitch = clamp(pitch - (e.clientY - dragY) * 0.0032, -1.15, 0.9);
    dragX = e.clientX; dragY = e.clientY;
  }
});
let dragging = false, dragX = 0, dragY = 0;
canvas.addEventListener('mousedown', e => { dragging = true; dragX = e.clientX; dragY = e.clientY; });
addEventListener('mouseup', () => { dragging = false; });

// touch look
let touchId = null;
canvas.addEventListener('touchstart', e => {
  if (state !== 'walk' || touchId !== null) return;
  const t = e.changedTouches[0];
  touchId = t.identifier; dragX = t.clientX; dragY = t.clientY;
}, { passive: true });
canvas.addEventListener('touchmove', e => {
  if (state !== 'walk') return;
  for (const t of e.changedTouches) {
    if (t.identifier === touchId) {
      yaw -= (t.clientX - dragX) * 0.0038;
      pitch = clamp(pitch - (t.clientY - dragY) * 0.0038, -1.15, 0.9);
      dragX = t.clientX; dragY = t.clientY;
    }
  }
}, { passive: true });
canvas.addEventListener('touchend', e => {
  for (const t of e.changedTouches) if (t.identifier === touchId) touchId = null;
}, { passive: true });

const walkHoldBtn = document.getElementById('walkHold');
for (const ev of ['touchstart', 'mousedown']) walkHoldBtn.addEventListener(ev, e => {
  e.preventDefault(); holdWalk = true; walkHoldBtn.classList.add('active');
});
for (const ev of ['touchend', 'touchcancel', 'mouseup']) walkHoldBtn.addEventListener(ev, () => {
  holdWalk = false; walkHoldBtn.classList.remove('active');
});

function updateWalk(dt) {
  const run = keys['ShiftLeft'] || keys['ShiftRight'];
  const speed = run ? 5.2 : 2.3;
  const fwd = (keys['KeyW'] || keys['ArrowUp'] || holdWalk ? 1 : 0) - (keys['KeyS'] || keys['ArrowDown'] ? 1 : 0);
  const strafe = (keys['KeyD'] || keys['ArrowRight'] ? 1 : 0) - (keys['KeyA'] || keys['ArrowLeft'] ? 1 : 0);

  const target = new THREE.Vector3(
    (Math.sin(yaw) * -fwd + Math.cos(yaw) * strafe) * speed,
    0,
    (Math.cos(yaw) * -fwd - Math.sin(yaw) * strafe) * speed
  );
  vel.lerp(target, Math.min(1, dt * 7));
  camera.position.x = clamp(camera.position.x + vel.x * dt, -20.5, 20.5);
  camera.position.z = clamp(camera.position.z + vel.z * dt, -66, 20);

  const moving = vel.length() > 0.25;
  if (moving) walkT += dt * (run ? 9 : 6.5);
  const bob = moving ? Math.sin(walkT) * 0.035 : 0;
  const ty = terrainH(camera.position.x, camera.position.z) + EYE + bob;
  camera.position.y = lerp(camera.position.y, ty, Math.min(1, dt * 9));
  camera.rotation.set(pitch, yaw, 0);
}

/* ============================================================ main loop */
const clock = new THREE.Clock();
function tick() {
  requestAnimationFrame(tick);
  const dt = Math.min(clock.getDelta(), 0.05);
  const t = clock.elapsedTime;
  uTime.value = t;

  // gentle light breath
  sun.intensity = 2.1 + Math.sin(t * 0.11) * 0.28;
  hemi.intensity = 0.75 + Math.sin(t * 0.11 + 1) * 0.06;

  updateBirds(t);

  if (state === 'cine') {
    const p = (performance.now() - cineStart) / 1000 / CINE_DUR;
    if (p >= 1) endCine();
    else applyCine(p);
  } else if (state === 'walk') {
    updateWalk(dt);
  }
  // 'veil' and 'hold': camera stays put, the world keeps breathing

  renderer.render(scene, camera);
}
tick();

addEventListener('resize', () => {
  camera.aspect = innerWidth / innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(innerWidth, innerHeight);
});
